export const YANDEX_AUTH_CALLBACK_PATH = '/yandex-auth'
export const YANDEX_OAUTH_MESSAGE_TYPE = 'kidsout:yandex-oauth'

/** No optional_scope passed: every scope is required, the user can't hide phone/email on the consent screen. */
const YANDEX_OAUTH_SCOPE = 'login:info login:email login:default_phone login:birthday'
const POPUP_TIMEOUT_MS = 5 * 60_000

export type YandexOauthResult = {
  code: string
  code_verifier: string
}

export type YandexOauthMessage = {
  type: typeof YANDEX_OAUTH_MESSAGE_TYPE
  code?: string
  state?: string
  error?: string
}

export class YandexPopupBlockedError extends Error {}
export class YandexPopupClosedError extends Error {}
export class YandexOauthFailedError extends Error {}

const base64url = (bytes: Uint8Array) =>
  btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')

const randomString = () => base64url(crypto.getRandomValues(new Uint8Array(32)))

const s256 = async (verifier: string) => base64url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))))

/**
 * Authorization Code + PKCE: the YANDEX_AUTH_CALLBACK_PATH page posts the code back
 * to the opener; code_verifier is sent to the backend with the code — the token
 * exchange happens server-to-server.
 */
export const runYandexOauthPopup = async ({ clientId }: { clientId: string }): Promise<YandexOauthResult> => {
  const codeVerifier = randomString()
  const state = randomString()
  const query = new URLSearchParams({
    response_type: 'code',
    client_id: clientId,
    redirect_uri: `${window.location.origin}${YANDEX_AUTH_CALLBACK_PATH}`,
    scope: YANDEX_OAUTH_SCOPE,
    state,
    code_challenge: await s256(codeVerifier),
    code_challenge_method: 'S256',
  })

  const popup = window.open(`https://oauth.yandex.ru/authorize?${query.toString()}`, 'kidsout-yandex-auth', 'width=600,height=700')
  if (!popup) throw new YandexPopupBlockedError('Yandex auth popup was blocked')

  return await new Promise<YandexOauthResult>((resolve, reject) => {
    const cleanup = () => {
      window.removeEventListener('message', onMessage)
      clearInterval(closeWatcher)
      clearTimeout(timeout)
    }

    const fail = (error: Error) => {
      cleanup()
      reject(error)
    }

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return
      const data = event.data as YandexOauthMessage | null
      if (!data || data.type !== YANDEX_OAUTH_MESSAGE_TYPE) return
      if (data.state !== state) return fail(new YandexOauthFailedError('state mismatch'))
      if (data.error || !data.code) return fail(new YandexOauthFailedError(data.error || 'no code returned'))
      cleanup()
      resolve({ code: data.code, code_verifier: codeVerifier })
    }

    const closeWatcher = setInterval(() => {
      if (popup.closed) fail(new YandexPopupClosedError('Yandex auth popup was closed'))
    }, 1000)
    const timeout = setTimeout(() => fail(new YandexOauthFailedError('timeout')), POPUP_TIMEOUT_MS)

    window.addEventListener('message', onMessage)
  })
}
